/**
 * A cook's bump, item bump and recall, written to paio-ordering's ticket
 * transition route (ORDR-42).
 *
 * `POST /api/v1/ordering/kds/tickets/<ticket_id>/transition/` with
 * `{transition_key, to_state, device_id}`. The route needs `ticket:advance`.
 *
 * THE PROMISE IS THE CONTRACT WITH THE FRAMEWORK. ui-components 0.3.4 moves the
 * card only when the promise resolves and shows its Alert when it rejects, so
 * every refusal here REJECTS: a 422 (illegal move, the body names the legal
 * set), any other non-200, a network failure, or a board action with no legal
 * server move. Nothing is optimistic.
 *
 * A FRESH `transition_key` PER COOK ACTION. The key is what makes a retry safe
 * on the server (a reused key answers `applied: false`), so it is minted once
 * per tap and never derived from the ticket, which a second, deliberate bump
 * of the same ticket would then collide with.
 *
 * ITEM BUMP. paio-ordering has no line-level route. A line bump on a READY
 * ticket that bags its LAST unbagged line completes the ticket (READY to
 * COMPLETED); an earlier line resolves without a server write, because the
 * server holds no per-line bag state to write. This is named in the PR.
 */

import type { ApiClient } from "../api/client.js";
import type { KitchenBrandLane, KitchenTicket, KitchenTicketItem } from "../framework/types.js";
import { bumpTarget, recallTarget, type TicketState } from "./ticketStates.js";

export class TicketTransitionRefused extends Error {
  readonly status: number | null;
  readonly ticketId: string;
  readonly toState: TicketState | null;
  readonly legalStates: readonly string[];

  constructor(
    message: string,
    details: { status: number | null; ticketId: string; toState: TicketState | null; legalStates?: readonly string[] },
  ) {
    super(message);
    this.name = "TicketTransitionRefused";
    this.status = details.status;
    this.ticketId = details.ticketId;
    this.toState = details.toState;
    this.legalStates = details.legalStates ?? [];
  }
}

export interface KitchenTransitionDeps {
  readonly client: ApiClient;
  readonly deviceId: string;
  /** The server state of a ticket as the feed last reported it. */
  readonly serverStateOf: (ticketId: string) => TicketState | undefined;
  /** Injected for tests. Defaults to `crypto.randomUUID`. */
  readonly newKey?: () => string;
}

export interface KitchenTransitions {
  onBump(lane: KitchenBrandLane, ticket: KitchenTicket): Promise<void>;
  onItemBump(lane: KitchenBrandLane, ticket: KitchenTicket, item: KitchenTicketItem): Promise<void>;
  onRecall(lane: KitchenBrandLane, ticket: KitchenTicket): Promise<void>;
}

function defaultKey(): string {
  return globalThis.crypto.randomUUID();
}

function legalStatesIn(body: unknown): readonly string[] {
  const record = body as Record<string, unknown> | null;
  const candidates = [
    record?.["legal_states"],
    (record?.["extensions"] as Record<string, unknown> | undefined)?.["legal_states"],
    (record?.["context"] as Record<string, unknown> | undefined)?.["legal_states"],
  ];
  const found = candidates.find((value) => Array.isArray(value));
  return Array.isArray(found) ? found.filter((entry): entry is string => typeof entry === "string") : [];
}

export function createKitchenTransitions(deps: KitchenTransitionDeps): KitchenTransitions {
  const newKey = deps.newKey ?? defaultKey;

  async function transition(ticket: KitchenTicket, target: (state: TicketState) => TicketState | null, verb: string): Promise<void> {
    const current = deps.serverStateOf(ticket.id);
    const toState = current === undefined ? null : target(current);
    if (current === undefined || toState === null) {
      throw new TicketTransitionRefused(`${ticket.reference} cannot be ${verb} from here.`, {
        status: null,
        ticketId: ticket.id,
        toState: null,
      });
    }

    const response = await deps.client.request(
      `/ordering/kds/tickets/${encodeURIComponent(ticket.id)}/transition/`,
      {
        method: "POST",
        json: { transition_key: newKey(), to_state: toState, device_id: deps.deviceId },
      },
    );

    if (response.status === 200) {
      return;
    }
    throw new TicketTransitionRefused(
      response.status === 422
        ? `${ticket.reference} was not ${verb}: the kitchen's orders refused that move.`
        : `${ticket.reference} was not ${verb}: the kitchen's orders did not answer.`,
      { status: response.status, ticketId: ticket.id, toState, legalStates: legalStatesIn(response.body) },
    );
  }

  return {
    onBump: (_lane, ticket) => transition(ticket, bumpTarget, "bumped"),
    onRecall: (_lane, ticket) => transition(ticket, recallTarget, "recalled"),
    async onItemBump(_lane, ticket, item) {
      const remaining = ticket.items.filter((line) => !line.bumped && line.id !== item.id);
      if (remaining.length > 0) {
        return;
      }
      await transition(ticket, (state) => (state === "READY" ? "COMPLETED" : null), "bagged");
    },
  };
}
